import React, { useState, useEffect } from "react";
import apiClient from "../apiClient";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useShop } from "../context/ShopContext";
import { FaSpinner } from "react-icons/fa";
import Sidebar from "./Sidebar";
import EmptyState from "./EmptyState";
import "./responsiveTable.css";

const ProductRateDifference = () => {
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const { shopId } = useShop();

  const showToast = (message, type = "error") => {
    toast.dismiss();
    if (type.toLowerCase() === "error") {
      toast.error(message);
    } else if (type.toLowerCase() === "success") {
      toast.success(message);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post(
        "/Liquor/GetTodaySalesProduct",
        {
          shopId: shopId,
          typeId: "1",
          stockDate: date || new Date().toISOString().split("T")[0],
        }
      );

      if (response.data?.code === 200 && Array.isArray(response.data.data)) {
        const formatted = response.data.data.map((p) => ({
          id: p.id,
          pName: p.pName,
          packing: p.packing,
          mrp: parseFloat(p.mrp) || 0,
          totalSale: Math.round(parseFloat(p.salesToday) || 0),
        }));
        setProducts(formatted);

        const initialRows = formatted.reduce((acc, p) => {
          acc[p.id] = { amount: "", remark: "" };
          return acc;
        }, {});
        setRows(initialRows);
      } else {
        showToast(response.data?.message || "Failed to fetch products", "error");
        setProducts([]);
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error fetching products", "error");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [date]);

  const handleRowChange = (id, field, value) => {
    if (field === "amount") {
      if (value !== "" && value !== "-" && isNaN(value)) return;
      if (value !== "" && value !== "-") {
        const product = products.find((p) => p.id === id);
        if (product && Math.abs(parseFloat(value)) > product.mrp) {
          showToast(`Rate diff per unit cannot exceed MRP (${product.mrp})`, "error");
          return;
        }
      }
    }
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleSubmit = async () => {
    const payload = products
      .map((p) => {
        const row = rows[p.id];
        const rateDiff = parseFloat(row?.amount);
        if (!row?.amount || isNaN(rateDiff) || rateDiff === 0) return null;
        const totalAmount = Math.abs(rateDiff) * p.totalSale;
        return {
          ProductId: p.id,
          ShopId: shopId,
          TypeId: rateDiff < 0 ? 2 : 1,
          Amount: totalAmount,
          Date: date,
          Remark: row.remark || "",
        };
      })
      .filter((item) => item !== null);

    if (payload.length === 0) {
      showToast("Please enter amount for at least one product", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(
        "/Liquor/SaveProductRateDiff",
        payload
      );

      if (response.data?.code === 200) {
        showToast("Data submitted successfully!", "success");
        fetchProducts();
      } else {
        showToast(response.data?.message || "Failed to submit data", "error");
      }
    } catch (error) {
      showToast("Error submitting data", "error");
    } finally {
      setLoading(false);
    }
  };

  const totalCredit = products.reduce((sum, p) => {
    const amount = parseFloat(rows[p.id]?.amount) || 0;
    return amount > 0 ? sum + amount * p.totalSale : sum;
  }, 0);

  const totalDebit = products.reduce((sum, p) => {
    const amount = parseFloat(rows[p.id]?.amount) || 0;
    return amount < 0 ? sum + Math.abs(amount) * p.totalSale : sum;
  }, 0);

  const isSubmitEnabled = Object.values(rows).some(
    (r) => r.amount && parseFloat(r.amount) !== 0 && !isNaN(parseFloat(r.amount))
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                Product Rate Difference
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="index.html" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-500 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Dashboards</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-xl-12">
                <div className="card card-flush h-md-100">
                  <ToastContainer />
                  <div className="card-body pt-6">
                    <div className="table-responsive">
                      <div className="panel-body">

                        <div className="form-group d-flex gap-5 mb-4">
                          <div className="col-lg-3 col-6">
                            <label className="form-label">Date:</label>
                            <input
                              type="date"
                              className="form-control"
                              value={date}
                              max={new Date().toISOString().split("T")[0]}
                              onChange={(e) => setDate(e.target.value)}
                            />
                          </div>
                          <div className="col-lg-3 col-6">
                            <label className="form-label">Search Product</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search product"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="clearfix"></div>
                        <br />

                        <div className="table-responsive h-700px entry-desktop">
                          {loading ? (
                            <div className="loader-container">
                              <FaSpinner className="spinner-icon" />
                            </div>
                          ) : products.length > 0 ? (
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>S.No.</th>
                                  <th>Product Name</th>
                                  <th>Rate</th>
                                  <th>Variant</th>
                                  <th>Qty</th>
                                  <th>Rate Diff/Unit</th>
                                  <th>Total Amt</th>
                                  <th>Remark</th>
                                </tr>
                              </thead>
                              <tbody>
                                {products
                                  .filter((p) =>
                                    p.pName.toLowerCase().includes(searchQuery.toLowerCase())
                                  )
                                  .map((product, index) => (
                                    <tr key={product.id}>
                                      <td>{index + 1}</td>
                                      <td>{product.pName}</td>
                                      <td>{product.mrp}</td>
                                      <td>{product.packing}</td>
                                      <td>{product.totalSale}</td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="0"
                                          value={rows[product.id]?.amount || ""}
                                          onChange={(e) =>
                                            handleRowChange(product.id, "amount", e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            if (["e", "E", "+"].includes(e.key)) {
                                              e.preventDefault();
                                            }
                                          }}
                                        />
                                      </td>
                                      <td>
                                        {(() => {
                                          const rd = parseFloat(rows[product.id]?.amount) || 0;
                                          const total = rd * product.totalSale;
                                          return total !== 0 ? (
                                            <span style={{ fontWeight: 600, color: total < 0 ? "#EF4444" : "#10B981" }}>
                                              {total.toFixed(2)}
                                            </span>
                                          ) : "-";
                                        })()}
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Remark"
                                          value={rows[product.id]?.remark || ""}
                                          onChange={(e) =>
                                            handleRowChange(product.id, "remark", e.target.value)
                                          }
                                        />
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          ) : (
                            <EmptyState />
                          )}
                        </div>

                        {/* Mobile: product cards */}
                        <div className="entry-mobile">
                          {loading ? (
                            <div className="loader-container"><FaSpinner className="spinner-icon" /></div>
                          ) : products.length === 0 ? (
                            <EmptyState />
                          ) : (
                            products
                              .filter((p) => p.pName.toLowerCase().includes(searchQuery.toLowerCase()))
                              .map((product, index) => (
                                <div key={product.id} className="ef-card">
                                  <div className="ef-card-header">
                                    <span className="ef-card-title">{product.pName}</span>
                                    <span className="ef-card-badge">{product.packing}</span>
                                  </div>
                                  <div className="ef-card-body">
                                    <div className="ef-info-row">
                                      <div className="ef-info-pill">
                                        <span className="ef-info-pill-label">Rate</span>
                                        <span className="ef-info-pill-value">{product.mrp}</span>
                                      </div>
                                      <div className="ef-info-pill">
                                        <span className="ef-info-pill-label">Qty</span>
                                        <span className="ef-info-pill-value">{product.totalSale}</span>
                                      </div>
                                      {(() => {
                                        const rd = parseFloat(rows[product.id]?.amount) || 0;
                                        const total = rd * product.totalSale;
                                        return total !== 0 ? (
                                          <div className="ef-info-pill">
                                            <span className="ef-info-pill-label">Total Amt</span>
                                            <span className="ef-info-pill-value" style={{ color: total < 0 ? "#EF4444" : "#10B981", fontWeight: 700 }}>
                                              {total.toFixed(2)}
                                            </span>
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                    <div className="ef-field">
                                      <span className="ef-field-label">Rate Diff/Unit (negative = Debit)</span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="0"
                                        value={rows[product.id]?.amount || ""}
                                        onChange={(e) => handleRowChange(product.id, "amount", e.target.value)}
                                        onKeyDown={(e) => {
                                          if (["e", "E", "+"].includes(e.key)) e.preventDefault();
                                        }}
                                      />
                                    </div>
                                    <div className="ef-field">
                                      <span className="ef-field-label">Remark</span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Remark"
                                        value={rows[product.id]?.remark || ""}
                                        onChange={(e) => handleRowChange(product.id, "remark", e.target.value)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))
                          )}
                        </div>

                        {/* ── Summary + Submit bar ── */}
                        <div style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          flexWrap: "wrap", gap: 12, marginTop: 16,
                          padding: "14px 18px",
                          background: "linear-gradient(135deg, #0f172a 0%, #1e2a45 100%)",
                          borderRadius: 12,
                          boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
                        }}>
                          {/* Totals row */}
                          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Credit</span>
                              <span style={{ fontSize: 15, fontWeight: 800, color: "#10B981" }}>₹{totalCredit.toFixed(2)}</span>
                            </div>
                            <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.1)" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Debit</span>
                              <span style={{ fontSize: 15, fontWeight: 800, color: "#EF4444" }}>₹{totalDebit.toFixed(2)}</span>
                            </div>
                            <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.1)" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366F1", flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Net</span>
                              <span style={{ fontSize: 17, fontWeight: 900, color: (totalCredit - totalDebit) >= 0 ? "#10B981" : "#EF4444" }}>
                                ₹{(totalCredit - totalDebit).toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {/* Submit button */}
                          <button
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={!isSubmitEnabled || loading}
                            style={{ minWidth: 110, fontWeight: 700 }}
                          >
                            {loading ? "Submitting…" : "✓ Submit"}
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="kt_app_footer" className="app-footer">
        <div className="app-container container-fluid d-flex flex-column flex-md-row flex-center flex-md-stack py-3">
          <div className="text-gray-900 order-2 order-md-1">
            <span className="text-muted fw-semibold me-1">2025&copy;</span>
            <a href="#" target="_blank" className="text-gray-800 text-hover-primary">WineShop</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductRateDifference;
